import { useCallback, useState } from 'react';
import {
  dealConfigSchema,
  projectRoleSchema,
  type DealConfig,
  type DealProbability,
  type DealReason,
  type DealStatus,
  type PlaceholderProfile,
  type ProjectRole,
} from '@forecastify/shared';
import { Tabs } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { DEAL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { parseList, usePermissionSet } from '../permissions-hook';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { Framed } from '../settings/settings-route';
import { SettingsPage } from '../settings/settings-page';
import { useFirstLoad } from '../use-first-load';
import { seniorityLabel } from './deal-kit';
import {
  ConfirmDelete,
  ProbabilityDialog,
  ProfileDialog,
  ReasonDialog,
  StatusDialog,
  type Deleting,
  type ReasonEditing,
} from './settings-deal-dialogs';
import { ForecastTab, ProbabilitiesTab, ReasonsTab, StatusesTab } from './settings-deal-tabs';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D1 · *Beállítások → Deal menedzsment*.            │
 * │ `phase6/16`–`19`, DM-1, DM-5.                                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Four tabs over one read (`GET /deal-settings`): the statuses, the probability steps, the
 * reasons, and the forecast categories with the deal settings and the placeholder profiles.
 * Reading needs `settings.view` (or `deal.view`); every write needs `settings.manage`, and a
 * reader without it sees no write control at all.
 *
 * ⚠️ **Every write reloads the whole config**, refused or not. A refusal is most often a stale
 * version or a row somebody else just used, and the cure for both is the server's current state.
 */

type Tab = 'statuses' | 'probabilities' | 'reasons' | 'forecast';
type Subject = 'status' | 'probability' | 'reason' | 'profile' | 'settings';

/**
 * The sentence for a refused settings write. ⚠️ Branches on `code` and the `reason` detail the
 * server put there (rule 5), never on the message.
 */
async function settingsRefusal(response: Response, t: Translate, subject: Subject) {
  const envelope = (await response.json().catch(() => null)) as {
    error?: { code?: string; details?: { field?: string; code?: string }[] };
  } | null;
  const code = envelope?.error?.code ?? 'unavailable';
  const reason = envelope?.error?.details?.find((detail) => detail.field === 'reason')?.code;

  if (code === 'conflict') {
    if (reason === 'closed_status') return t('dealSettings.error.closedStatus');
    if (reason === 'last_open') return t('dealSettings.error.lastOpen');
    if (reason === 'in_use') {
      if (subject === 'status') return t('dealSettings.error.inUse.status');
      if (subject === 'reason') return t('dealSettings.error.inUse.reason');
      if (subject === 'profile') return t('dealSettings.error.inUse.profile');
      return t('dealSettings.error.inUse.other');
    }
    /* No reason: the stale-version conflict. */
    return t('dealSettings.error.stale');
  }
  if (code === 'name_taken') {
    if (subject === 'probability') return t('dealSettings.error.nameTaken.probability');
    if (subject === 'reason') return t('dealSettings.error.nameTaken.reason');
    if (subject === 'profile') return t('dealSettings.error.nameTaken.profile');
    return t('dealSettings.error.nameTaken.status');
  }
  if (code === 'not_found') return t('dealSettings.error.notFound');
  if (code === 'validation_failed') return t('dealSettings.error.invalid');
  if (code === 'permission_denied') return t('dealSettings.error.denied');
  return t('error.unavailable');
}

export function SettingsDealsRoute() {
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();
  const canManage = permissions.has('settings.manage');

  const [config, setConfig] = useState<DealConfig | null>(null);
  const [roles, setRoles] = useState<readonly ProjectRole[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('statuses');

  const [editingStatus, setEditingStatus] = useState<DealStatus | 'new' | null>(null);
  const [editingProbability, setEditingProbability] = useState<DealProbability | 'new' | null>(
    null,
  );
  const [editingReason, setEditingReason] = useState<ReasonEditing>(null);
  const [editingProfile, setEditingProfile] = useState<PlaceholderProfile | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Deleting | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const [response, roleResponse] = await Promise.all([
      session.request('/deal-settings').catch(() => null),
      session.request('/project-roles').catch(() => null),
    ]);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = dealConfigSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setConfig(parsed.data);
    setFailure(null);
    /* ⚠️ The roles only feed the profile dialog's picker — a failed read leaves it empty rather
       than failing the section. */
    if (roleResponse?.ok === true) {
      setRoles(parseList(projectRoleSchema, await roleResponse.json()) ?? []);
    }
  }, [session]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(DEAL_TOPICS, reload);

  /** One write: the refusal sentence, or `null` once the config is re-read. */
  const send = async (
    path: string,
    init: RequestInit,
    subject: Subject,
  ): Promise<string | null> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) return t('error.unavailable');
    const refusal = response.ok ? null : await settingsRefusal(response, t, subject);
    await reload();
    return refusal;
  };

  /** A write from the page itself — a switch, a reorder — whose refusal shows above the table. */
  const act = (path: string, init: RequestInit, subject: Subject): void => {
    void send(path, init, subject).then(setServerError);
  };

  const json = (method: string, body: unknown): RequestInit => ({
    method,
    body: JSON.stringify(body),
  });

  const statuses = config?.statuses ?? [];
  const probabilities = config?.probabilities ?? [];
  const reasons = config?.reasons ?? [];
  const currency = config?.settings.currencyCode ?? 'HUF';
  const lostGroups = [
    ...new Set(
      reasons
        .filter((reason) => reason.kind === 'lost' && reason.groupName !== '')
        .map((reason) => reason.groupName),
    ),
  ];

  const action =
    !canManage || config === null
      ? undefined
      : tab === 'statuses'
        ? {
            label: t('dealSettings.statuses.new'),
            icon: 'plus' as const,
            onSelect: () => {
              setEditingStatus('new');
            },
          }
        : tab === 'probabilities'
          ? {
              label: t('dealSettings.probabilities.new'),
              icon: 'plus' as const,
              onSelect: () => {
                setEditingProbability('new');
              },
            }
          : tab === 'reasons'
            ? {
                label: t('dealSettings.reasons.newLost'),
                icon: 'plus' as const,
                onSelect: () => {
                  setEditingReason({ new: 'lost' });
                },
              }
            : {
                label: t('dealSettings.profiles.new'),
                icon: 'plus' as const,
                onSelect: () => {
                  setEditingProfile('new');
                },
              };

  const secondaryAction =
    canManage && config !== null && tab === 'reasons'
      ? {
          label: t('dealSettings.reasons.newWon'),
          icon: 'plus' as const,
          onSelect: () => {
            setEditingReason({ new: 'won' });
          },
        }
      : undefined;

  return (
    <Framed section="deals">
      <div data-screen="settings-deals" className="flex min-h-0 flex-1 flex-col">
        <ScreenState failure={failure} onRetry={() => void reload()}>
          <SettingsPage
            title={t('settings.nav.deals')}
            action={action}
            secondaryAction={secondaryAction}
            error={serverError}
            tabs={
              <Tabs
                variant="underline"
                className="border-0"
                label={t('settings.nav.deals')}
                value={tab}
                onValueChange={(value) => {
                  setServerError(null);
                  setTab(
                    value === 'probabilities' || value === 'reasons' || value === 'forecast'
                      ? value
                      : 'statuses',
                  );
                }}
                items={[
                  {
                    value: 'statuses',
                    label: t('dealSettings.tab.statuses'),
                    count: statuses.length,
                  },
                  {
                    value: 'probabilities',
                    label: t('dealSettings.tab.probabilities'),
                    count: probabilities.length,
                  },
                  { value: 'reasons', label: t('dealSettings.tab.reasons'), count: reasons.length },
                  { value: 'forecast', label: t('dealSettings.tab.forecast') },
                ]}
              />
            }
          >
            {tab === 'statuses' ? (
              <StatusesTab
                statuses={statuses}
                loading={loading}
                canManage={canManage}
                onEdit={setEditingStatus}
                onReorder={(ids) => {
                  act('/deal-settings/statuses/order', json('PUT', { ids }), 'status');
                }}
                onDelete={(status) => {
                  setDeleting({
                    title: t('dealSettings.statuses.delete'),
                    body: t('dealSettings.statuses.deleteBody', { name: status.name }),
                    confirm: () =>
                      send(`/deal-settings/statuses/${status.id}`, { method: 'DELETE' }, 'status'),
                  });
                }}
              />
            ) : tab === 'probabilities' ? (
              <ProbabilitiesTab
                probabilities={probabilities}
                loading={loading}
                canManage={canManage}
                onEdit={setEditingProbability}
                onDelete={(step) => {
                  setDeleting({
                    title: t('dealSettings.probabilities.delete'),
                    body: t('dealSettings.probabilities.deleteBody', { name: step.name }),
                    confirm: () =>
                      send(
                        `/deal-settings/probabilities/${step.id}`,
                        { method: 'DELETE' },
                        'probability',
                      ),
                  });
                }}
              />
            ) : tab === 'reasons' ? (
              <ReasonsTab
                reasons={reasons}
                loading={loading}
                canManage={canManage}
                onEdit={setEditingReason}
                onToggle={(reason: DealReason, patch) => {
                  act(
                    `/deal-settings/reasons/${reason.id}`,
                    json('PUT', {
                      kind: reason.kind,
                      name: reason.name,
                      groupName: reason.groupName,
                      active: patch.active ?? reason.active,
                      needsNote: patch.needsNote ?? reason.needsNote,
                      version: reason.version,
                    }),
                    'reason',
                  );
                }}
                onDelete={(reason) => {
                  setDeleting({
                    title: t('dealSettings.reasons.delete'),
                    body: t('dealSettings.reasons.deleteBody', { name: reason.name }),
                    confirm: () =>
                      send(`/deal-settings/reasons/${reason.id}`, { method: 'DELETE' }, 'reason'),
                  });
                }}
              />
            ) : (
              <ForecastTab
                config={config}
                loading={loading}
                canManage={canManage}
                onSettings={(next) => {
                  act('/deal-settings', json('PATCH', next), 'settings');
                }}
                onEditProfile={setEditingProfile}
                onDeleteProfile={(profile) => {
                  setDeleting({
                    title: t('dealSettings.profiles.delete'),
                    body: t('dealSettings.profiles.deleteBody', {
                      name: `${profile.projectRoleName} · ${seniorityLabel(t, profile.seniority)}`,
                    }),
                    confirm: () =>
                      send(
                        `/deal-settings/profiles/${profile.id}`,
                        { method: 'DELETE' },
                        'profile',
                      ),
                  });
                }}
              />
            )}
          </SettingsPage>
        </ScreenState>

        <StatusDialog
          editing={editingStatus}
          onClose={() => {
            setEditingStatus(null);
          }}
          onSubmit={(body) =>
            editingStatus === 'new' || editingStatus === null
              ? send('/deal-settings/statuses', json('POST', body), 'status')
              : send(`/deal-settings/statuses/${editingStatus.id}`, json('PUT', body), 'status')
          }
        />
        <ProbabilityDialog
          editing={editingProbability}
          onClose={() => {
            setEditingProbability(null);
          }}
          onSubmit={(body) =>
            editingProbability === 'new' || editingProbability === null
              ? send('/deal-settings/probabilities', json('POST', body), 'probability')
              : send(
                  `/deal-settings/probabilities/${editingProbability.id}`,
                  json('PUT', body),
                  'probability',
                )
          }
        />
        <ReasonDialog
          editing={editingReason}
          groups={lostGroups}
          onClose={() => {
            setEditingReason(null);
          }}
          onSubmit={(body) =>
            editingReason === null || 'new' in editingReason
              ? send('/deal-settings/reasons', json('POST', body), 'reason')
              : send(`/deal-settings/reasons/${editingReason.id}`, json('PUT', body), 'reason')
          }
        />
        <ProfileDialog
          editing={editingProfile}
          roles={roles}
          currencyCode={currency}
          onClose={() => {
            setEditingProfile(null);
          }}
          onSubmit={(body) =>
            editingProfile === 'new' || editingProfile === null
              ? send('/deal-settings/profiles', json('POST', body), 'profile')
              : send(`/deal-settings/profiles/${editingProfile.id}`, json('PUT', body), 'profile')
          }
        />
        <ConfirmDelete
          deleting={deleting}
          onClose={() => {
            setDeleting(null);
          }}
        />
      </div>
    </Framed>
  );
}
